/**
 * A saved program stores only what the TRAINER chose — `exerciseId` plus the prescription (sets,
 * reps, rest, tempo). The exercise's NAME and IMAGES live in the exercise library, so anything that
 * DISPLAYS a program has to resolve them first.
 *
 * Two screens need that: the builder ({@link ../pages/NewTemplatePage}) and the studio board
 * ({@link ../pages/TemplatePresentPage}), and the board is unusable without the images — so the
 * fetch+merge lives here instead of being copied per page.
 *
 * `fromLibrary=true` on the lookup is what lets a COMPANY_GROUP_ADMIN resolve the SYSTEM exercises
 * its program references; scoped to its own library alone, none of them would resolve and every
 * card would render as "תרגיל 1234" with no picture.
 */

import { getExercise } from '../services/ExcersiceService';
import type { ExerciseLike } from './exerciseUtils';

/**
 * The exercise shape after enrichment. Identical to the permissive {@link ExerciseLike} the
 * exercise utils already bridge every server shape with — an enriched exercise IS a template
 * exercise with the library's name/media filled in, not a different kind of thing, and giving it
 * its own near-identical interface would only mean casting between the two at every call.
 */
export type EnrichedExercise = ExerciseLike;

export interface EnrichedWorkout {
    id?: number | null;
    index?: number;
    name?: string | null;
    nameHeb?: string | null;
    exercises?: EnrichedExercise[];
    [key: string]: unknown;
}

export interface EnrichedTemplate {
    id?: number;
    name?: string;
    nameHeb?: string;
    gender?: string;
    experienceLevel?: string;
    bodyPart?: string;
    secondaryMuscle?: string;
    equipmentType?: string;
    trainDuration?: string;
    goals?: string[] | Set<string>;
    systemResource?: boolean;
    ownerUserId?: number;
    ownerName?: string;
    workouts?: EnrichedWorkout[];
    [key: string]: unknown;
}

/**
 * Fills in each exercise's name and media from the exercise library, and lifts per-set rep ranges
 * out of `intervals` into the flat `setRanges` the editors read.
 *
 * Mutates and returns the template it is given (the builder already relied on that), and never
 * rejects on a single unresolvable exercise — one deleted exercise must not blank a whole program.
 */
export const enrichTemplateExercises = async (
    templateData: EnrichedTemplate | null | undefined
): Promise<EnrichedTemplate | null | undefined> => {
    if (!templateData?.workouts) return templateData;

    const exerciseIds = new Set<number>();
    templateData.workouts.forEach(workout => {
        workout.exercises?.forEach(exercise => {
            if (exercise.exerciseId) exerciseIds.add(exercise.exerciseId);
        });
    });

    const exerciseDefinitions = (
        await Promise.all(
            Array.from(exerciseIds).map(id =>
                getExercise(id, true)
                    .then((res: { data?: EnrichedExercise } | EnrichedExercise) =>
                        (res as { data?: EnrichedExercise })?.data || (res as EnrichedExercise))
                    .catch(() => null)
            )
        )
    ).filter(Boolean) as EnrichedExercise[];

    const exerciseMap = exerciseDefinitions.reduce<Record<number, EnrichedExercise>>((map, ex) => {
        if (ex.id != null) map[ex.id] = ex;
        return map;
    }, {});

    templateData.workouts.forEach(workout => {
        workout.exercises?.forEach(exercise => {
            if (exercise.intervals && exercise.intervals.length > 0) {
                const hasCustomRanges = exercise.intervals.some(
                    interval => interval.minReps != null || interval.maxReps != null
                );
                if (hasCustomRanges) {
                    exercise.setRanges = exercise.intervals.map((interval, idx) => ({
                        setIndex: idx,
                        minReps: interval.minReps,
                        maxReps: interval.maxReps,
                    }));
                }

                // A catalyst's duration range is saved onto every set, but the editor reads (and
                // the save re-spreads) the flat defaultMin/MaxDuration. Without lifting it back, the
                // editor shows its 30-60 fallback and the next save overwrites the trainer's range.
                const timed = exercise.intervals.find(
                    interval => interval.minDuration != null || interval.maxDuration != null
                        || interval.recommendedDuration != null
                );
                if (timed) {
                    // Same for the optional recommended time; a blank one comes back null, not a midpoint.
                    if (exercise.recommendedDuration === undefined) {
                        exercise.recommendedDuration = timed.recommendedDuration ?? null;
                    }
                    if (exercise.defaultMinDuration == null && timed.minDuration != null) {
                        exercise.defaultMinDuration = timed.minDuration;
                    }
                    if (exercise.defaultMaxDuration == null && timed.maxDuration != null) {
                        exercise.defaultMaxDuration = timed.maxDuration;
                    }
                }
            }

            const def = exercise.exerciseId != null ? exerciseMap[exercise.exerciseId] : undefined;
            if (def) {
                if (!exercise.exerciseName) {
                    exercise.exerciseName = def.nameHeb || def.name;
                }
                if (!exercise.imageUrl) {
                    exercise.imageUrl = def.imageUrl || null;
                }
                if (!exercise.coverUrl) {
                    exercise.coverUrl = def.coverUrl || null;
                }
                // The board labels each card with the muscle it emphasises, and a program saved
                // before the field existed carries none — take the library's.
                if (!exercise.targetMuscle && def.targetMuscle) {
                    exercise.targetMuscle = def.targetMuscle;
                }
                if (!exercise.resistanceType && def.resistanceType) {
                    exercise.resistanceType = def.resistanceType;
                }
                // The rep RANGE lives on the exercise, not on the program: a template set stores
                // min/max only when the trainer typed one, and the server fills the rest from these
                // when it turns the template into a trainee's program (AdminProgramService). Without
                // them the board can only show the builder's placeholder "10" for every exercise.
                if (exercise.defaultMinReps == null && def.defaultMinReps != null) {
                    exercise.defaultMinReps = def.defaultMinReps;
                }
                if (exercise.defaultMaxReps == null && def.defaultMaxReps != null) {
                    exercise.defaultMaxReps = def.defaultMaxReps;
                }
            }
        });
    });

    return templateData;
};

export default enrichTemplateExercises;
